import { useEffect, useState } from "react";
import axios from "axios";
import { Link, Copy, ArrowsClockwise, ShareNetwork, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareSection = () => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const shareUrl = token ? `${window.location.origin}/s/${token}` : "";

  const fetchToken = async () => {
    try {
      const res = await axios.get(`${API}/share-info`, { withCredentials: true });
      setToken(res.data.token);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToken();
  }, []);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Enlace copiado al portapapeles");
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Enlace copiado");
      } catch {
        toast.error("No se pudo copiar el enlace");
      }
      document.body.removeChild(ta);
    }
  };

  const handleRegenerate = async () => {
    if (!confirm("¿Generar un nuevo enlace? El enlace anterior dejará de funcionar.")) return;
    setRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/share/regenerate`,
        {},
        { withCredentials: true }
      );
      setToken(res.data.token);
      toast.success("Nuevo enlace generado", {
        description: "El enlace anterior ya no es válido.",
      });
    } catch (e) {
      console.error(e);
      toast.error("Error al regenerar el enlace");
    } finally {
      setRegenerating(false);
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: "Estado de AquíSigo",
        text: "Mira mi estado de seguridad en tiempo real:",
        url: shareUrl,
      });
    } catch {
      // user cancelled, ignore
    }
  };

  return (
    <section
      className="bg-surface border border-border rounded-xl p-5 md:p-6"
      data-testid="share-section"
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShareNetwork size={20} weight="duotone" className="text-aqua" />
          <h2 className="font-heading font-bold text-lg tracking-tight">
            Enlace público de estado
          </h2>
        </div>
        {token && (
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-aqua hover:text-aqua/80 transition-colors"
            data-testid="share-preview-link"
          >
            <Eye size={14} weight="bold" />
            Vista previa
          </a>
        )}
      </div>

      <p className="text-sm text-white/60 mb-4">
        Comparte este enlace con tu familia o amigos para que puedan ver tu estado en
        tiempo real, sin recibir emails. La página pública no muestra tus contactos ni
        información sensible.
      </p>

      {loading ? (
        <div className="text-sm text-white/45">Generando enlace...</div>
      ) : (
        <>
          <div
            className="flex items-stretch gap-2 bg-background border border-border rounded-lg p-1 mb-3"
            data-testid="share-url-row"
          >
            <div className="flex items-center gap-2 px-3 flex-1 min-w-0">
              <Link size={16} weight="bold" className="text-aqua shrink-0" />
              <input
                type="text"
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="mono font-mono text-xs bg-transparent border-0 outline-none text-white/85 w-full truncate"
                data-testid="share-url-input"
              />
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="px-3 py-2 rounded-md bg-aqua text-background hover:bg-aqua/90 active:bg-aqua/80 transition-colors text-xs font-bold uppercase tracking-[0.2em] inline-flex items-center gap-1.5"
              data-testid="share-copy-btn"
            >
              <Copy size={14} weight="bold" /> Copiar
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleNativeShare}
              className="px-3 py-2 rounded-md border border-border hover:border-aqua/50 transition-colors text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white inline-flex items-center gap-1.5"
              data-testid="share-native-btn"
            >
              <ShareNetwork size={14} weight="bold" /> Compartir
            </button>
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={regenerating}
              className="px-3 py-2 rounded-md border border-border hover:border-danger/50 hover:text-danger transition-colors text-xs uppercase tracking-[0.2em] text-white/60 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="share-regenerate-btn"
            >
              <ArrowsClockwise
                size={14}
                weight="bold"
                className={regenerating ? "animate-spin" : ""}
              />
              {regenerating ? "Generando..." : "Regenerar"}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default ShareSection;
